import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import "@/pages/public/turni.css";
import { fmtShort, fmtDay, addDays, groupByWeek, endDate, jsDay, currentShift, calendarLinks, fmtLong } from "@/lib/vigilanza";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PublicTurni() {
  const { data, isError, isLoading } = useQuery({
    queryKey: ["public-turni"], refetchInterval: 5 * 60_000,
    queryFn: async () => (await axios.get(`${BACKEND}/api/public/turni`)).data,
  });
  const [slotId, setSlotId] = useState(null);
  useEffect(() => { document.title = data?.title || "Turni Vigilanza"; if (data && !slotId && data.slots.length) setSlotId(data.slots[0].id); }, [data, slotId]);

  if (isLoading) return <div className="turni"><div className="wrap"><p className="sub">Caricamento…</p></div></div>;
  if (isError || !data) return <div className="turni"><div className="wrap"><h1>Turni non disponibili</h1></div></div>;
  const slot = data.slots.find((s) => s.id === slotId) || data.slots[0];

  return (
    <div className="turni" data-testid="public-turni">
      <div className="wrap">
        <header>
          <span className="eyebrow">{data.eyebrow}</span>
          <h1>{data.title}</h1>
          <p className="sub">Dal {fmtLong(data.period_from)} al {fmtLong(data.period_to)}{slot && <> · turno <b>{slot.start_time} → {slot.end_time}</b>{slot.end_time <= slot.start_time ? " del mattino dopo" : ""}</>}</p>
        </header>
        {data.slots.length > 1 && (
          <div className="slot-tabs" role="tablist">
            {data.slots.map((s) => (
              <button key={s.id} role="tab" aria-selected={s.id === slot?.id} onClick={() => setSlotId(s.id)} data-testid={`public-turni-tab-${s.id}`}>
                {s.name} <span className="t">{s.start_time}–{s.end_time}</span>
              </button>
            ))}
          </div>
        )}
        {slot ? <SlotBoard key={slot.id} slot={slot} eyebrow={data.eyebrow}/> : <p className="sub">Nessuna fascia attiva.</p>}
        <footer>Il turno in corso è evidenziato. La pagina si aggiorna da sola.</footer>
      </div>
    </div>
  );
}

function SlotBoard({ slot, eyebrow }) {
  const [sel, setSel] = useState(null);
  const [cur, setCur] = useState(currentShift(slot));
  useEffect(() => { const t = setInterval(() => setCur(currentShift(slot)), 60_000); return () => clearInterval(t); }, [slot]);
  useEffect(() => { const el = document.getElementById("turno-attuale"); if (el && el.getBoundingClientRect().top > window.innerHeight) el.scrollIntoView({ block: "center", behavior: "smooth" }); }, []);
  const counts = useMemo(() => { const c = {}; slot.days.forEach((d) => { if (d.who) c[d.who] = (c[d.who] || 0) + 1; }); return c; }, [slot]);
  const people = Object.keys(counts).sort((a, b) => a.localeCompare(b));
  const unit = (n) => (slot.end_time <= slot.start_time ? (n === 1 ? "notte" : "notti") : (n === 1 ? "turno" : "turni"));

  return (
    <>
      <section>
        <h2>Chi presta servizio</h2>
        <div className="people">
          {people.map((p) => (
            <button key={p} className="person" aria-pressed={sel === p} onClick={() => setSel(sel === p ? null : p)} data-testid={`public-turni-person-${p}`}>
              <span>{p}</span><span className="n">{counts[p]} {unit(counts[p])}</span>
            </button>
          ))}
        </div>
        {people.length > 0 ? <p className="hint">Tocca il tuo nome: vedrai i tuoi turni con i pulsanti per aggiungerli a Google Calendar o Outlook.</p> : <p className="hint">Nessun turno ancora assegnato.</p>}
      </section>
      <section>
        <h2>Calendario</h2>
        <div className="list">
          {groupByWeek(slot.days).map((w) => (
            <div className="week" key={w.monday}>
              <h3>Settimana {fmtShort(w.monday)} – {fmtShort(addDays(w.monday, 6))}</h3>
              <div className="rows">{w.items.map((d) => <DayRow key={d.date} d={d} slot={slot} sel={sel} cur={cur} eyebrow={eyebrow}/>)}</div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

function DayRow({ d, slot, sel, cur, eyebrow }) {
  const now = cur.date === d.date && !d.excluded;
  const wk = [0, 6].includes(jsDay(d.date));
  const end = endDate(d.date, slot);
  const cls = ["row", wk && "weekend", now && "now", d.excluded && "off", sel && (d.who === sel ? "hit" : "dim")].filter(Boolean).join(" ");
  const links = sel && d.who === sel ? calendarLinks(d.date, slot, d.who, eyebrow) : null;
  return (
    <div className={cls} id={now ? "turno-attuale" : undefined} data-testid={`public-turni-row-${d.date}`}>
      <div className="date"><span className="d">{fmtDay(d.date)}</span><span className="t">{end !== d.date ? `→ ${fmtDay(end)} ${slot.end_time}` : `${slot.start_time} → ${slot.end_time}`}</span></div>
      <div className="who">
        {d.excluded ? <span className="none">Nessun turno{d.excluded_note ? ` · ${d.excluded_note}` : ""}</span> : (d.who || <span className="none">Da assegnare</span>)}
        {now && <span className="tag">{cur.label}</span>}
        {d.note && <span className="note">{d.note}</span>}
        {links && (
          <span className="cal">
            <a href={links.google} target="_blank" rel="noopener noreferrer" data-testid={`public-turni-gcal-${d.date}`}>+ Google Calendar</a>
            <a href={links.outlook} target="_blank" rel="noopener noreferrer" data-testid={`public-turni-outlook-${d.date}`}>+ Outlook</a>
          </span>
        )}
      </div>
    </div>
  );
}
